import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION_NAME = 'testimonials';

/**
 * Testimonial Schema:
 * {
 *   id: string (auto-generated by Firestore)
 *   name: string (required) - Client's full name
 *   role: string (optional) - Client's job title/role
 *   location: string (optional) - Client's location (city, state)
 *   content: string (required) - Testimonial text/content
 *   rating: number (required, 1-5) - Star rating
 *   highlight: string (optional) - Service/category highlight (e.g., "Retirement Planning")
 *   email: string (optional) - Client's email (for admin purposes, not displayed)
 *   status: string (required) - "pending" | "approved" | "rejected" (default: "pending")
 *   createdAt: Timestamp (auto-generated) - When testimonial was submitted
 *   updatedAt: Timestamp (auto-generated) - Last update time
 *   approvedAt: Timestamp (optional) - When testimonial was approved
 *   approvedBy: string (optional) - Admin who approved it
 *   ipAddress: string (optional) - For spam prevention
 *   isFeatured: boolean (default: false) - Featured testimonials shown first
 * }
 */

/**
 * Submit a new testimonial
 * @param {Object} testimonialData - Testimonial data object
 * @returns {Promise<string>} - Document ID of the created testimonial
 */
export async function submitTestimonial(testimonialData) {
  if (!db) {
    throw new Error('Firebase is not initialized. Please check your configuration.');
  }

  const {
    name,
    role = '',
    location = '',
    content,
    rating,
    highlight = '',
    email = '',
    ipAddress = ''
  } = testimonialData;

  // Validation
  if (!name || !content || !rating) {
    throw new Error('Name, content, and rating are required fields.');
  }

  if (rating < 1 || rating > 5) {
    throw new Error('Rating must be between 1 and 5.');
  }

  const testimonial = {
    name: name.trim(),
    role: role.trim(),
    location: location.trim(),
    content: content.trim(),
    rating: parseInt(rating, 10),
    highlight: highlight.trim(),
    email: email.trim(),
    status: 'pending', // New testimonials need approval
    isFeatured: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(ipAddress && { ipAddress })
  };

  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), testimonial);
    return docRef.id;
  } catch (error) {
    console.error('Error submitting testimonial:', error);
    throw new Error('Failed to submit testimonial. Please try again.');
  }
}

/**
 * Get all approved testimonials
 * @param {Object} options - Query options
 * @param {number} options.limitCount - Maximum number of testimonials to return
 * @param {boolean} options.featuredFirst - Show featured testimonials first
 * @returns {Promise<Array>} - Array of testimonial objects
 */
export async function getApprovedTestimonials(options = {}) {
  if (!db) {
    console.warn('Firebase is not initialized. Returning empty array.');
    return [];
  }

  const { limitCount = 50, featuredFirst = true } = options;

  try {
    const snap = await getDocs(collection(db, COLLECTION_NAME));
    const testimonials = [];

    snap.forEach((doc) => {
      const data = doc.data();
      const status = data.status || 'approved'; // Default to approved if status is missing
      if (status === 'approved') {
        testimonials.push({
          id: doc.id,
          name: data.name,
          role: data.role || '',
          location: data.location || '',
          content: data.content,
          rating: data.rating || 5,
          highlight: data.highlight || '',
          isFeatured: data.isFeatured || false,
          createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date().toISOString(),
        });
      }
    });

    // In-memory sort: featured first, then newest
    testimonials.sort((a, b) => {
      if (featuredFirst) {
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

    return testimonials.slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    return [];
  }
}

/**
 * Get all testimonials (including pending) - Admin only
 * @returns {Promise<Array>} - Array of all testimonial objects
 */
export async function getAllTestimonials() {
  if (!db) {
    console.warn('Firebase is not initialized. Returning empty array.');
    return [];
  }

  try {
    const snap = await getDocs(collection(db, COLLECTION_NAME));
    const testimonials = [];

    snap.forEach((doc) => {
      const data = doc.data();
      testimonials.push({
        id: doc.id,
        ...data,
        status: data.status || 'approved',
        createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
        approvedAt: data.approvedAt?.toDate?.() || data.approvedAt,
      });
    });

    // In-memory sort newest first
    testimonials.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    return testimonials;
  } catch (error) {
    console.error('Error fetching all testimonials:', error);
    return [];
  }
}

/**
 * Get pending testimonials count - Admin only
 * @returns {Promise<number>} - Number of pending testimonials
 */
export async function getPendingCount() {
  const pending = await getPendingTestimonials();
  return pending.length;
}

/**
 * Get pending testimonials - Admin only
 * @returns {Promise<Array>} - Array of pending testimonial objects
 */
export async function getPendingTestimonials() {
  const all = await getAllTestimonials();
  return all.filter(t => t.status === 'pending');
}

/**
 * Approve a testimonial - Admin only
 * @param {string} testimonialId - Document ID of the testimonial
 * @param {string} approvedBy - Admin identifier
 * @returns {Promise<void>}
 */
export async function approveTestimonial(testimonialId, approvedBy = 'admin') {
  if (!db) {
    throw new Error('Firebase is not initialized.');
  }

  const { doc, updateDoc, getDoc } = await import('firebase/firestore');
  
  try {
    const testimonialRef = doc(db, COLLECTION_NAME, testimonialId);
    const testimonialSnap = await getDoc(testimonialRef);
    
    if (!testimonialSnap.exists()) {
      throw new Error('Testimonial not found.');
    }

    await updateDoc(testimonialRef, {
      status: 'approved',
      approvedAt: serverTimestamp(),
      approvedBy: approvedBy,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error approving testimonial:', error);
    throw new Error('Failed to approve testimonial. ' + error.message);
  }
}

/**
 * Reject a testimonial - Admin only
 * @param {string} testimonialId - Document ID of the testimonial
 * @param {string} rejectedBy - Admin identifier
 * @returns {Promise<void>}
 */
export async function rejectTestimonial(testimonialId, rejectedBy = 'admin') {
  if (!db) {
    throw new Error('Firebase is not initialized.');
  }

  const { doc, updateDoc } = await import('firebase/firestore');
  
  try {
    const testimonialRef = doc(db, COLLECTION_NAME, testimonialId);
    
    await updateDoc(testimonialRef, {
      status: 'rejected',
      updatedAt: serverTimestamp(),
      rejectedBy: rejectedBy
    });
  } catch (error) {
    console.error('Error rejecting testimonial:', error);
    throw new Error('Failed to reject testimonial. ' + error.message);
  }
}

/**
 * Delete a testimonial - Admin only
 * @param {string} testimonialId - Document ID of the testimonial
 * @returns {Promise<void>}
 */
export async function deleteTestimonial(testimonialId) {
  if (!db) {
    throw new Error('Firebase is not initialized.');
  }

  const { doc, deleteDoc } = await import('firebase/firestore');
  
  try {
    const testimonialRef = doc(db, COLLECTION_NAME, testimonialId);
    await deleteDoc(testimonialRef);
  } catch (error) {
    console.error('Error deleting testimonial:', error);
    throw new Error('Failed to delete testimonial. ' + error.message);
  }
}

/**
 * Toggle featured status of a testimonial - Admin only
 * @param {string} testimonialId - Document ID of the testimonial
 * @param {boolean} isFeatured - Featured status
 * @returns {Promise<void>}
 */
export async function setFeaturedTestimonial(testimonialId, isFeatured) {
  if (!db) {
    throw new Error('Firebase is not initialized.');
  }

  const { doc, updateDoc } = await import('firebase/firestore');
  
  try {
    const testimonialRef = doc(db, COLLECTION_NAME, testimonialId);
    
    await updateDoc(testimonialRef, {
      isFeatured: isFeatured,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating featured status:', error);
    throw new Error('Failed to update featured status. ' + error.message);
  }
}

